﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// A page for the first step in the password reset workflow. Displays an input field where the user can
// enter their username (email or phone number).

// Can be tested in jRåd.
// Click on the "Forgot username or password" link at the login page.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { TitleText } from "~base/Components/Primitives/TitleText";
import Grid from "~base/Components/Layout/Grid";
import { useIntl } from "~base/Internationalization/IntlContext";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import classnames from "classnames";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import Container from "~base/Components/Layout/Container";
import TitleButton from "~base/Components/Primitives/TitleButton";
import { useStyles } from "./Styles";
import { reducer } from "./Store";
import { useAccountPostReset } from "~base/Services/API/Account/PostReset";
import { useSettings } from "~base/Settings/SettingsContext";
import Form from "~base/Components/Primitives/Form";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import { useNavigateService } from "~base/Services/NavigateService";
import TextBlock from "~base/Components/Primitives/TextBlock";
import { ApiStatus, getErrorMessage } from "~base/Services/API";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type ResetProps = unknown;

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const ResetPage: React.FunctionComponent<ResetProps> = (
    props: ResetProps
) => {
    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("ResetPage"),
        reducer,
        { username: "", errorMessage: null, loading: false }
    );
    const classes = useStyles();

    const navigate = useNavigateService();
    const settings = useSettings();
    const serverVirtualPath = settings.valueAsString("serverVirtualPath");

    const jssSpacingStyles = useJssSpacingStyles();

    const formVerticalSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        horizontalCenterPadding
    );

    const intl = useIntl();

    const detectedTenant = settings.valueAsString("detectedTenantName");

    const [loadingAccountResetApi, invokeAccountResetApi] = useAccountPostReset(
        {
            callback: async (status: ApiStatus, response: Response) => {
                const tenantPart = detectedTenant ? `/${detectedTenant}` : "";
                const resetUrlBase = `${serverVirtualPath}Account/ResetCode${tenantPart}?`;

                const resetCodeUrl = `${resetUrlBase}username=${encodeURIComponent(state.username)}`;
                if (status == "Success") {
                    dispatch({ type: "setLoading" });
                    navigate.current(resetCodeUrl);
                } else {
                    dispatch({
                        type: "setError",
                        errorMessage: await getErrorMessage(intl, response),
                    });
                }
            },
        }
    );

    const isLoading = loadingAccountResetApi || state.loading;

    const formIsValid = state.username.length > 0;

    return (
        <ApplicationPage isLoading={isLoading}>
            <Form
                onSubmit={() => {
                    if (state.errorMessage)
                        dispatch({ type: "setError", errorMessage: null });
                    invokeAccountResetApi({
                        username: state.username,
                        tenant: detectedTenant,
                    });
                }}
                isValid={formIsValid}
                testIdentifier="ResetForm"
            >
                <Grid templateColumns="">
                    <Container className={classes.containerGridSetup}>
                        <Rubrik
                            textCode="VisiFinance.UI.Reset.Title"
                            rubrikSizeType="Medium"
                        />
                        <TextBlock
                            styleType="Paragraph"
                            textCode="VisiFinance.UI.Reset.Description"
                        />
                    </Container>

                    <TitleText
                        titleTextCode="VisiFinance.UI.Reset.Username"
                        value={state.username}
                        name={"Username"}
                        autoFocus={true}
                        setValue={(x) =>
                            dispatch({
                                type: "updateFormCredentials",
                                username: x,
                            })
                        }
                        testIdentifier="VisiFinance.UI.Reset.Username"
                    />

                    <TitleButton
                        title=" "
                        submit={true}
                        styleType={"DefaultPadded"}
                        enabled={formIsValid}
                        buttonClassName={classnames(formVerticalSpacingClasses)}
                        testIdentifier="ResetSend"
                    >
                        {intl.formatText("VisiFinance.UI.Reset.Send")}
                    </TitleButton>

                    {state.errorMessage && (
                        <TextBlock
                            styleType="Paragraph"
                            size="Small"
                            styleSubType="Validation"
                            text={state.errorMessage}
                        />
                    )}
                </Grid>
            </Form>
        </ApplicationPage>
    );
};
